import {
  Avatar,
  Badge,
  Box,
  CollapseCard,
  FlexBox,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useMemo } from 'react';

import { ConversationCustomFieldsForm } from 'components/conversation/conversationCustomFieldsForm';
import { getEmptyCustomFields, useCustomFields } from 'utils/customFields';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useCurrentUserInAcw } from 'utils/user';

import styles from './conversationCustomFieldsCard.module.scss';

type ConversationCustomFieldsCardProps = {
  csid: number;
  isMinimized: boolean;
  isOpen: boolean;
  onToggle: () => void;
};

export const ConversationCustomFieldsCard = ({
  csid,
  isMinimized,
  isOpen,
  onToggle,
}: ConversationCustomFieldsCardProps): JSX.Element | null => {
  const { customFields, rawResponse } = useCustomFields({
    entityId: 'conversation',
    entityInstanceId: `${csid}`,
  });

  const { requiredCustomAttribute } = useFeatureFlag([
    'required-custom-attribute',
  ]);

  const isCurrentUserInAcw = useCurrentUserInAcw({ csid });

  const missingRequiredAttributesCount = getEmptyCustomFields({
    customFields,
    onlyRequired: true,
  }).length;

  const badge = useMemo(() => {
    if (!requiredCustomAttribute) {
      return null;
    }

    if (!missingRequiredAttributesCount) {
      return null;
    }

    return (
      <Tooltip
        dark
        content={
          <FlexBox flexDirection="column" gap="100">
            <Box>
              {missingRequiredAttributesCount} required{' '}
              {missingRequiredAttributesCount > 1
                ? 'attributes (*) are'
                : 'attribute (*) is'}{' '}
              missing value.
            </Box>

            {isCurrentUserInAcw && (
              <Box>
                Wrap-up time will not end until all required arributes are
                filled in.
              </Box>
            )}
          </FlexBox>
        }
      >
        <Badge
          className={classnames({ [styles.badge]: !isMinimized })}
          variant="warning"
          size="small"
        >
          {missingRequiredAttributesCount} *
        </Badge>
      </Tooltip>
    );
  }, [
    isCurrentUserInAcw,
    isMinimized,
    missingRequiredAttributesCount,
    requiredCustomAttribute,
  ]);

  const inputIcon = <Avatar icon="input" />;

  if (!customFields.length || !rawResponse.attributes?.isSuccess) {
    return null;
  }

  if (isMinimized) {
    return (
      <FlexBox flexDirection="column" gap="100">
        <div className="icon-holder">{inputIcon}</div>
        {badge}
      </FlexBox>
    );
  }

  return (
    <CollapseCard isOpen={isOpen} onToggle={onToggle}>
      <CollapseCard.Header avatar={inputIcon}>
        Conversation attributes
        {badge}
      </CollapseCard.Header>
      <CollapseCard.Content>
        <ConversationCustomFieldsForm customFields={customFields} csid={csid} />
      </CollapseCard.Content>
    </CollapseCard>
  );
};
